/**
 * Workspace-credentials API client.
 *
 * Wraps `/agents/credentials` — the encrypted secrets manager the user
 * uses to store provider tokens (Claude OAuth, Claude API key, etc).
 *
 * Read paths return MASKED rows only — server never echoes plaintext.
 * Add path uploads plaintext over HTTPS; server immediately encrypts
 * with KMS and stores the ciphertext. The frontend never persists the
 * plaintext beyond the in-memory React state for the modal.
 *
 * Backend reference: backend/src/handlers/workspace-credentials.js
 */

import api from './api';

const BASE = '/agents/credentials';

const WorkspaceCredentials = {
  /**
   * List all credentials for the calling user (masked).
   * @returns {Promise<{ credentials: [], by_type: { oauth: [], api: [] }, total: number }>}
   */
  list() {
    return api.get(BASE);
  },

  /**
   * Add a new credential. Server dedupes by token_hash so re-uploading
   * the same token is idempotent.
   *
   * @param {{ type: 'oauth' | 'api', token: string, provider?: 'claude' | 'openai', source?: string }} body
   *   provider defaults to 'claude' for back-compat with the pre-Codex
   *   paste-token modal. OpenAI (Codex v1) only supports type='api'.
   */
  add({ type, token, provider = 'claude', source = 'web:settings' }) {
    if (!type || !['oauth', 'api'].includes(type)) {
      return Promise.reject(new Error('type must be "oauth" or "api"'));
    }
    if (!['claude', 'openai'].includes(provider)) {
      return Promise.reject(new Error('provider must be "claude" or "openai"'));
    }
    if (provider === 'openai' && type !== 'api') {
      return Promise.reject(new Error('OpenAI credentials only support type=api (no OAuth in v1)'));
    }
    if (!token || typeof token !== 'string' || token.length < 8) {
      return Promise.reject(new Error('token missing or too short'));
    }
    return api.post(BASE, { type, token, provider, source });
  },

  remove({ type, index }) {
    return api.delete(`${BASE}/${encodeURIComponent(type)}/${encodeURIComponent(index)}`);
  },

  /**
   * Pre-flight: does the user have at least one Claude credential
   * (either kind)? Drives the Deploy modal's State A vs State B branch
   * and the inline "using token …abc123" line.
   *
   * Filters by `cred.provider === 'claude'` so that OpenAI rows (added
   * via the OpenAI paste-token modal post-Codex) don't accidentally
   * satisfy a Claude check. Legacy rows without a provider field are
   * treated as Claude (matches the backend resolveAgentCredential
   * fallback).
   *
   * @returns {Promise<{ hasClaude: boolean, mode?: 'oauth' | 'api', suffix?: string }>}
   */
  async getClaudeStatus() {
    try {
      const res = await this.list();
      const all = res?.credentials || [];
      const claude = all.filter((c) => !c.provider || c.provider === 'claude');
      // Oauth wins per resolveAgentCredential's lookup order on the
      // backend — keep this consistent so the UI shows the same suffix
      // the deployed task will actually use.
      const oauth = claude.find((c) => c.type === 'oauth');
      if (oauth) return { hasClaude: true, mode: 'oauth', suffix: oauth.masked || '' };
      const apiKey = claude.find((c) => c.type === 'api');
      if (apiKey) return { hasClaude: true, mode: 'api', suffix: apiKey.masked || '' };
      return { hasClaude: false };
    } catch {
      // On error (401, network), assume no creds so the UI nudges the
      // user to set one. Worse-case is an extra paste-token step that
      // would otherwise have been silent.
      return { hasClaude: false };
    }
  },

  /**
   * Same shape as getClaudeStatus but for OpenAI (Codex v1). API-key
   * only — no OAuth path. Drives the Deploy modal's Codex State A vs
   * State B branch when provider='codex' is picked.
   *
   * @returns {Promise<{ hasOpenAI: boolean, mode?: 'api', suffix?: string }>}
   */
  async getOpenAIStatus() {
    try {
      const res = await this.list();
      const all = res?.credentials || [];
      const apiKey = all.find((c) => c.provider === 'openai' && c.type === 'api');
      if (apiKey) return { hasOpenAI: true, mode: 'api', suffix: apiKey.masked || '' };
      return { hasOpenAI: false };
    } catch {
      return { hasOpenAI: false };
    }
  },
};

export default WorkspaceCredentials;
